import React, { useState } from 'react'
import {
  CCol,
  CContainer,
  CForm,
  CFormInput,
  CFormSelect,
  CFormLabel,
  CButton,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CCard,
  CCardBody,
  CButtonGroup,
} from '@coreui/react'
import { useDispatch, useSelector } from 'react-redux'
import { addNavigation, deleteNavigation, updateNavigation } from 'src/redux/navigation'
import { cilPencil, cilXCircle } from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import swal from 'sweetalert'

const flattenNavigation = (data) => {
  const flat = []

  data?.forEach((nav) => {
    flat.push({ ...nav, parent: '' })
    if (nav.type === 'group' && nav.items) {
      nav.items.forEach((item) => {
        flat.push({ ...item, name: `↳ ${item.name}`, parent: nav.name })
      })
    }
  })

  return flat
}

const Navigation = () => {
  const dispatch = useDispatch()
  const { navigationData } = useSelector((state) => state.adminnavigation)
  const flatNav = flattenNavigation(navigationData)

  const [form, setForm] = useState({
    type: 'group',
    name: '',
    to: '',
    icon: '',
    parent_id: '',
  })
  const [editMode, setEditMode] = useState(false)
  const [editingId, setEditingId] = useState(null)

  const resetForm = () => {
    setForm({ type: 'group', name: '', to: '', icon: '', parent_id: '' })
    setEditMode(false)
    setEditingId(null)
  }

  const handleChange = ({ target: { name, value } }) => {
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const payload = {
      ...form,
      id: editingId,
      parent_id: form.type === 'item' ? form.parent_id : null,
    }

    try {
      if (editMode) {
        await dispatch(updateNavigation(payload))
      } else {
        await dispatch(addNavigation(payload))
      }
      resetForm()
    } catch (err) {
      console.error(err)
    }
  }

  const handleEdit = (item) => {
    setForm({
      type: item.type,
      name: item.name.replace(/^↳ /, ''),
      to: item.to,
      icon: item.icon,
      parent_id: navigationData.find((group) => group.name === item.parent)?.id || '',
    })
    setEditMode(true)
    setEditingId(item.id)
  }

  return (
    <div className="bg-light d-flex flex-row align-items-start">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md={9}>
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <CTable bordered hover responsive>
                <CTableHead>
                  <CTableRow>
                    {['ID', 'Type', 'Name', 'Path', 'Icon', 'Parent', 'Actions'].map((head, i) => (
                      <CTableHeaderCell key={i}>{head}</CTableHeaderCell>
                    ))}
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {flatNav?.map((item) => (
                    <CTableRow
                      key={item.id}
                      style={
                        item.type === 'group'
                          ? { fontWeight: 'bold', backgroundColor: '#f7f7f7' }
                          : {}
                      }
                    >
                      <CTableDataCell>{item.id}</CTableDataCell>
                      <CTableDataCell>{item.type}</CTableDataCell>
                      <CTableDataCell>{item.name}</CTableDataCell>
                      <CTableDataCell>{item.to}</CTableDataCell>
                      <CTableDataCell>{item.icon}</CTableDataCell>
                      <CTableDataCell>{item.parent}</CTableDataCell>
                      <CTableDataCell>
                        <CButtonGroup>
                          <CButton size="sm" color="warning" onClick={() => handleEdit(item)}>
                            <CIcon icon={cilPencil} className="text-white" />
                          </CButton>
                          <CButton
                            color="danger"
                            size="sm"
                            onClick={() =>
                              swal({
                                title: 'Are you sure?',
                                text: 'You want to delete this navigation!',
                                icon: 'warning',
                                buttons: ['No', 'Yes'],
                                dangerMode: true,
                              }).then((willDelete) => {
                                if (willDelete) {
                                  dispatch(deleteNavigation(item.id))
                                }
                              })
                            }
                          >
                            <CIcon icon={cilXCircle} className="text-white" />
                          </CButton>
                        </CButtonGroup>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </div>
          </CCol>

          <CCol md={3}>
            <CCard>
              <CCardBody>
                <h5>{editMode ? 'Edit' : 'Add'} Navigation</h5>
                <CForm onSubmit={handleSubmit}>
                  <CFormLabel>Type</CFormLabel>
                  <CFormSelect name="type" value={form.type} onChange={handleChange}>
                    <option value="group">Group</option>
                    <option value="item">Item</option>
                  </CFormSelect>

                  <CFormLabel className="mt-2">Name</CFormLabel>
                  <CFormInput name="name" value={form.name} onChange={handleChange} required />

                  {form.type === 'item' && (
                    <>
                      <CFormLabel className="mt-2">To (Path)</CFormLabel>
                      <CFormInput name="to" value={form.to} onChange={handleChange} required />

                      <CFormLabel className="mt-2">Parent Group</CFormLabel>
                      <CFormSelect name="parent_id" value={form.parent_id} onChange={handleChange}>
                        <option value="">Select Parent Group</option>
                        {navigationData
                          .filter((nav) => nav.type === 'group')
                          .map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.name}
                            </option>
                          ))}
                      </CFormSelect>
                    </>
                  )}

                  {(form.type === 'group' || !form.parent_id) && (
                    <>
                      <CFormLabel className="mt-2">Icon</CFormLabel>
                      <CFormInput name="icon" value={form.icon} onChange={handleChange} />
                    </>
                  )}

                  <CButton type="submit" color="primary" className="mt-3 me-2">
                    {editMode ? 'Update' : 'Add'} Navigation
                  </CButton>
                  <CButton type="button" color="secondary" className="mt-3" onClick={resetForm}>
                    Clear
                  </CButton>
                </CForm>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default Navigation
